import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobileViewport } from './useIsMobileViewport';

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * The scrim behind the open drawer is a real "Close navigation" button, so it belongs in the
 * drawer's tab cycle, but it paints nothing and would show no focus ring. Opening the drawer
 * therefore skips it and lands on the first navigation control instead.
 */
const BACKDROP_SELECTOR = '.forge-sidebar-backdrop';

/**
 * A kit overlay opened from inside the drawer — the tool and environment menus in the footer, a
 * dialog, a select — sits above it and owns the keyboard while it is open. Radix dismisses its own
 * layer on the *document* and this hook listens on `window`, which fires afterwards, so without a
 * guard a single Escape would close the menu *and* the drawer underneath it.
 *
 * The guard cannot be "is an overlay open right now": Radix's `DismissableLayer` flips its content
 * to `data-state="closed"` before this listener runs, so by here the menu already looks shut. It
 * does, however, `preventDefault()` the Escape it consumed, so `defaultPrevented` is the reliable
 * signal that a layer above already answered the key. (Measured in Chromium: `defaultPrevented`
 * true, open-overlay count already 0. jsdom orders the two differently, so the unit test alone did
 * not catch this — the browser walk did.)
 *
 * The DOM check stays as the second half of the test for keys a layer handles without preventing
 * the default, such as Tab.
 */
const OPEN_OVERLAY_SELECTOR = ['menu', 'dialog', 'alertdialog', 'listbox']
  .map((role) => `[role="${role}"][data-state="open"]`)
  .join(', ');

function hasOverlayAboveDrawer(event: KeyboardEvent): boolean {
  return event.defaultPrevented || document.querySelector(OPEN_OVERLAY_SELECTOR) !== null;
}

function tabbableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
  );
}

export interface MobileNavDrawer {
  /** True at or below the mobile breakpoint, where the shell renders a top bar plus a drawer. */
  isMobile: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Goes on `.forge-mobile-nav-button`; focus returns here when the drawer closes. */
  openerRef: RefObject<HTMLButtonElement>;
  /** Goes on `.forge-sidebar-shell`, which must also carry `tabIndex={-1}` as a focus fallback. */
  drawerRef: RefObject<HTMLDivElement>;
}

/**
 * Open/close state and focus management for the mobile navigation drawer shared by the Forge,
 * Eval, and Archon shells. The drawer is modal — a scrim covers the page behind it — so while it
 * is open Tab cycles inside it, Escape closes it, and focus moves in on open and back to the
 * opener on close — unless a kit overlay is open above it, which takes the keyboard instead.
 * Closed-drawer inertness is CSS (`visibility: hidden` on `.forge-sidebar-shell`).
 */
export function useMobileNavDrawer(pathname: string): MobileNavDrawer {
  const isMobile = useIsMobileViewport();
  const [isOpen, setIsOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => setIsOpen(false), [pathname]);

  useEffect(() => {
    if (!isMobile) setIsOpen(false);
  }, [isMobile]);

  const isDrawerOpen = isMobile && isOpen;

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      // The overlay on top owns both Escape and Tab; the drawer only acts when it is the top layer.
      if (hasOverlayAboveDrawer(event)) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;

      const drawer = drawerRef.current;
      if (!drawer) return;
      const stops = tabbableElements(drawer);
      if (stops.length === 0) {
        event.preventDefault();
        drawer.focus();
        return;
      }

      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (!active || !drawer.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const drawer = drawerRef.current;
    const opener = openerRef.current;
    const entry = drawer
      ? tabbableElements(drawer).find((element) => !element.matches(BACKDROP_SELECTOR))
      : undefined;
    (entry ?? drawer)?.focus();

    return () => {
      // A shell that unmounted, or dropped its top bar on the way back to the desktop layout,
      // has no opener left to return to; leave focus wherever the next screen puts it.
      if (!opener?.isConnected) return;
      const active = document.activeElement;
      // Something outside the drawer already claimed focus (a page control, a dialog): leave it.
      if (active && active !== document.body && !drawer?.contains(active)) return;
      opener.focus();
    };
  }, [isDrawerOpen]);

  return { isMobile, isOpen: isDrawerOpen, open, close, openerRef, drawerRef };
}
